import {
  Background,
  BaseEdge,
  Controls,
  type Edge,
  EdgeLabelRenderer,
  type EdgeProps,
  Handle,
  MarkerType,
  MiniMap,
  type Node,
  type NodeProps,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  useUpdateNodeInternals,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  type ChartNode,
  type ChartThread,
  fetchBusinessLines,
  fetchChart,
  fetchChartThread,
  fetchProcesses,
  searchEmployees,
  type ThreadHop,
  type ThreadStep,
  type UserProfile,
} from './api'
import { ancestorsOf, layout, NODE_HEIGHT, NODE_WIDTH } from './chartLayout'
import EmployeeCreatePanel from './EmployeeCreatePanel'
import EmployeePanel from './EmployeePanel'

/**
 * Цвета нитей.
 *
 * Отдельная замкнутая палитра, а не цвета направлений: направление красит карточку, и
 * нить, взявшая тот же цвет, слилась бы с ней. Процессов на схеме одновременно немного,
 * поэтому четырёх различимых цветов хватает, а пятый пойдёт по кругу.
 */
const THREAD_COLORS = ['--oc-accent', '--oc-teal', '--oc-amber', '--oc-rose'] as const

const threadColor = (index: number) =>
  `var(${THREAD_COLORS[index % THREAD_COLORS.length]})`

/** Шаг вместе с процессом, которому он принадлежит: на карточке их может быть несколько. */
interface PlacedStep {
  step: ThreadStep
  processId: number
  processName: string
  color: string
  /** Идентификатор точки соединения. Уникален внутри карточки. */
  handle: string
}

interface PlacedHop {
  hop: ThreadHop
  processId: number
  color: string
}

const handleOf = (processId: number, stepKey: string) => `${processId}:${stepKey}`

/**
 * Размеры поля с кружками — **единственное** их определение.
 *
 * Отсюда считаются и высота карточки, и число кружков в ряду, и подрезка концов дуг;
 * CSS получает те же значения переменными (см. `THREAD_CSS_VARS`), а не повторяет их.
 * Пока числа стояли в двух местах, они разъезжались: стили задавали кружок в `rem`,
 * подрезка считала радиус в пикселях, и совпадали они только при шрифте ровно в 16px.
 *
 * Пиксели, а не `rem`, потому что от этих величин зависит геометрия на холсте, а `rem`
 * задаётся настройками браузера и в расчёт раскладки не приходит.
 */
const THREAD = {
  /** Диаметр кружка шага. */
  dot: 22,
  /** Промежуток между кружками — он же длина связки внутри карточки. */
  gap: 8,
  /** Черта над полем плюс воздух вокруг неё. */
  top: 12,
  /** Место под кнопку сворачивания: она в правом нижнем углу, то есть над полем. */
  toggle: 22,
  /** Горизонтальные поля самой карточки. */
  cardPaddingX: 11,
  /** Толщина самой нити. Тонкая намеренно: это нить, а не связь в дереве. */
  stroke: 1.2,
} as const

/** Те же величины для CSS. Ставятся один раз на холст и наследуются всеми карточками. */
const THREAD_CSS_VARS = {
  '--thread-dot': `${THREAD.dot}px`,
  '--thread-gap': `${THREAD.gap}px`,
  '--thread-top': `${THREAD.top}px`,
  '--thread-toggle': `${THREAD.toggle}px`,
  '--org-node-pad-x': `${THREAD.cardPaddingX}px`,
} as React.CSSProperties

const DOT_RADIUS = THREAD.dot / 2

/**
 * Сколько кружков помещается в ряд.
 *
 * Выводится из ширины карточки, а не подбирается: иначе у процесса с десятком шагов на
 * одном человеке кружки переносились бы на ряд, которого нет в расчёте высоты, и поле
 * обрезалось бы.
 */
const DOTS_PER_ROW = Math.max(
  1,
  Math.floor(
    (NODE_WIDTH - THREAD.cardPaddingX * 2 - THREAD.toggle + THREAD.gap) /
      (THREAD.dot + THREAD.gap),
  ),
)

/** Насколько карточка вырастает, раскрывая поле с кружками шагов. */
function threadFieldHeight(count: number): number {
  if (count === 0) return 0
  const rows = Math.ceil(count / DOTS_PER_ROW)
  return THREAD.top + rows * THREAD.dot + (rows - 1) * THREAD.gap
}

// React Flow требует, чтобы data была индексируемой по строке.
type FlowNodeData = ChartNode & {
  hasChildren: boolean
  collapsed: boolean
  highlighted: boolean
  /** Шаги процессов, легшие на этот узел. Пусто — узел ни в одной нити не участвует. */
  steps: PlacedStep[]
  /** Нить показывается: узлы вне неё приглушаются, чтобы путь читался. */
  threading: boolean
  onToggle: (id: string) => void
  onOpen: (node: ChartNode) => void
  [key: string]: unknown
}

type FlowBackgroundData = {
  business_line: string | null
  color: string
  [key: string]: unknown
}

/**
 * Класс цвета узла.
 *
 * Цвет приходит с сервера ключом палитры (`indigo`, `amber`, …), а не готовым значением:
 * у каждого ключа две версии — для светлой и тёмной темы, и живут они в CSS. Набор ключей
 * закрыт (`LineColor` на бэкенде), поэтому правил в стилях ровно столько же; настраивается
 * не палитра, а то, какому направлению какой ключ достался.
 */
const lineClass = (color: string) => `line-${color}`

/**
 * Цвета узлов в миниатюре.
 *
 * React Flow кладёт `fill` инлайновым стилем, поэтому перекрасить миниатюру одним CSS
 * нельзя — цвет приходится отдавать пропом. Отдаём не готовый цвет, а `var(--oc-…)`:
 * инлайновый стиль резолвит кастомные свойства из `:root`, и светлая/тёмная тема
 * продолжает работать сама, без чтения вычисленных стилей из JS.
 *
 * Карточки красятся насыщенным цветом направления, а не «белым с цветной полоской слева»,
 * как на холсте: в миниатюре полоска шириной в пиксель неразличима, и схема выглядела бы
 * россыпью белых квадратов — ровно то, что и было.
 */
function minimapNodeColor(node: Node): string {
  const color = String((node.data as { color?: unknown }).color ?? '')
  if (!color) return 'var(--oc-border-strong)'
  // Область подчинения — подложка: приглушённый тон, чтобы карточки поверх неё читались.
  return node.type === 'orgBackground' ? `var(--oc-${color}-soft)` : `var(--oc-${color})`
}

function CollapseToggle({ data }: { data: FlowNodeData }) {
  if (!data.hasChildren) return null
  return (
    <button
      type="button"
      className="collapse-toggle"
      title={data.collapsed ? 'Развернуть ветку' : 'Свернуть ветку'}
      onClick={(event) => {
        event.stopPropagation()
        data.onToggle(data.id)
      }}
    >
      {data.collapsed ? '+' : '−'}
    </button>
  )
}

/**
 * Поле с кружками шагов, раскрывающееся внутри карточки.
 *
 * Нить проходит через кружки, а не через саму карточку: карточка — это человек или
 * подразделение, и стрелка, упирающаяся в её край, не говорит, какой именно этап там
 * происходит. Поэтому точки соединения висят на кружках, а не на карточке, и на каждом
 * кружке их две — входящая и исходящая — в одном и том же месте, чтобы дуга начиналась
 * и заканчивалась ровно в центре кружка.
 *
 * `position` здесь ни на что не влияет: точка соединения имеет нулевой размер (см.
 * `.thread-dot-handle` в стилях), поэтому все её грани — это одна и та же середина
 * кружка. Указан он только потому, что React Flow требует значение.
 *
 * Шаг, разрешённый через руководителя подразделения, помечен пунктиром: работа назначена
 * отделу целиком, а руководитель — лишь способ показать её на схеме, и выдавать вывод за
 * названного исполнителя нельзя.
 */
function ThreadField({ steps }: { steps: PlacedStep[] }) {
  if (steps.length === 0) return null

  return (
    <div className="thread-field">
      {steps.map(({ step, processName, color, handle }) => (
        <span
          key={handle}
          className={
            step.resolved_via === 'department_head' ? 'thread-dot is-inferred' : 'thread-dot'
          }
          style={{ '--thread-color': color } as React.CSSProperties}
          title={
            `${processName} · шаг ${step.order}: ${step.title}` +
            (step.resolved_via === 'department_head'
              ? ' — назначено подразделению, показано на руководителе'
              : '')
          }
        >
          <Handle
            type="target"
            id={handle}
            position={Position.Top}
            className="thread-dot-handle"
            isConnectable={false}
          />
          <Handle
            type="source"
            id={handle}
            position={Position.Top}
            className="thread-dot-handle"
            isConnectable={false}
          />
          {step.order}
        </span>
      ))}
    </div>
  )
}

/** Лист: сотрудник без (развёрнутых) подчинённых или свёрнутое подразделение. */
function OrgNode({ data }: NodeProps<Node<FlowNodeData>>) {
  // Руководитель — тот, у кого есть прямые подчинённые. Явный
  // `department.head_employee_id` для выделения не используется намеренно: глава
  // подразделения почти всегда и так имеет подчинённых, то есть отдельная метка завела бы
  // второй визуальный язык почти без новой информации. Зато «с подчинёнными, но не
  // глава» — обычное дело, и таких выделить нужно.
  const isManager = data.kind === 'employee' && data.reports_count > 0

  const classes = [
    'org-node',
    lineClass(data.color),
    data.kind === 'department' ? 'is-department' : '',
    isManager ? 'is-manager' : '',
    data.highlighted ? 'is-highlighted' : '',
    // Приглушаем всё, что в нить не вошло: иначе путь теряется среди остальных карточек.
    data.threading && data.steps.length === 0 ? 'is-dimmed' : '',
    data.steps.length > 0 ? 'is-on-thread' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classes} onClick={() => data.onOpen(data)}>
      {/* Подсказка с полным именем: карточка фиксированной ширины, и длинные ФИО
          усекаются многоточием независимо от выделения руководителей. */}
      <div className="org-node-title" title={data.title}>
        {data.title}
      </div>
      {/* Счётчик стоит на строке должности, а не рядом с именем: в заголовке он
          отъедал ширину, и фамилии начинали обрываться многоточием. Имя важнее. */}
      <div className="org-node-sub">
        <span className="org-node-role">
          {data.kind === 'department' ? `${data.headcount} чел.` : data.subtitle}
        </span>
        {isManager && (
          <span
            className="org-node-reports"
            title={`Прямых подчинённых: ${data.reports_count}`}
          >
            {data.reports_count}
          </span>
        )}
      </div>
      <ThreadField steps={data.steps} />
      <CollapseToggle data={data} />
    </div>
  )
}

/** Просвет между кружком и остриём стрелки. Ровно настолько, чтобы они не слипались. */
const ARROW_GAP = 2

/**
 * Дуга нити.
 *
 * Своё ребро, а не готовый `smoothstep`: ступенчатая линия читается как связь в дереве —
 * ровно то, чем нить не является, — и на схеме, где узлы стоят где угодно, ломается о
 * чужие карточки. Дуга выгибается перпендикулярно отрезку, поэтому две стрелки между
 * одной парой кружков не сливаются.
 *
 * **Концы подрезаются на радиус кружка.** React Flow отдаёт координаты точек соединения, а
 * они сидят в центре кружка; рисуй мы дугу до них — остриё стрелки легло бы поверх номера
 * шага и закрыло его. Поэтому оба конца сдвигаются вдоль касательной наружу, и стрелка
 * упирается в край кружка, а не в его середину.
 */
function ThreadArc({
  sourceX,
  sourceY,
  targetX,
  targetY,
  label,
  style,
  markerEnd,
}: EdgeProps) {
  const dx = targetX - sourceX
  const dy = targetY - sourceY
  // Ноль случается, когда оба кружка ещё не измерены React Flow: делить на него нельзя.
  const distance = Math.hypot(dx, dy) || 1
  const bulge = Math.min(80, Math.max(22, distance * 0.18))
  const controlX = (sourceX + targetX) / 2 - (dy / distance) * bulge
  const controlY = (sourceY + targetY) / 2 + (dx / distance) * bulge

  // Касательные квадратичной кривой: в начале она идёт к контрольной точке, в конце —
  // от неё. Подрезка вдоль них, а не вдоль хорды: у выгнутой дуги это разные направления,
  // и по хорде конец уехал бы с кривой.
  const trim = Math.min(DOT_RADIUS + ARROW_GAP, distance * 0.4)
  const startLength = Math.hypot(controlX - sourceX, controlY - sourceY) || 1
  const endLength = Math.hypot(targetX - controlX, targetY - controlY) || 1

  const fromX = sourceX + ((controlX - sourceX) / startLength) * trim
  const fromY = sourceY + ((controlY - sourceY) / startLength) * trim
  const toX = targetX - ((targetX - controlX) / endLength) * trim
  const toY = targetY - ((targetY - controlY) / endLength) * trim

  const path = `M ${fromX},${fromY} Q ${controlX},${controlY} ${toX},${toY}`
  // Середина квадратичной кривой при t = 0.5.
  const labelX = 0.25 * fromX + 0.5 * controlX + 0.25 * toX
  const labelY = 0.25 * fromY + 0.5 * controlY + 0.25 * toY

  return (
    <>
      <BaseEdge path={path} markerEnd={markerEnd} style={style} />
      {label && (
        <EdgeLabelRenderer>
          <div
            className="thread-hop-label"
            style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  )
}

/**
 * Связка двух кружков внутри одной карточки — прямая, без стрелки.
 *
 * Дуга здесь неуместна: работа никуда не переходила, сменился лишь этап у того же
 * человека, — а выгиб на два десятка пикселей выглядел бы петлёй. Стрелки нет по той же
 * причине, по которой нет дуги: остриё между соседними кружками просто не помещается, а
 * порядок и так читается по номерам.
 *
 * Концы подрезаны на радиус кружка, поэтому линия упирается в края и кружки выглядят
 * нанизанными на неё.
 */
function ThreadLine({ sourceX, sourceY, targetX, targetY, style }: EdgeProps) {
  const dx = targetX - sourceX
  const dy = targetY - sourceY
  const distance = Math.hypot(dx, dy) || 1
  const trim = Math.min(DOT_RADIUS, distance * 0.45)

  const fromX = sourceX + (dx / distance) * trim
  const fromY = sourceY + (dy / distance) * trim
  const toX = targetX - (dx / distance) * trim
  const toY = targetY - (dy / distance) * trim

  return <BaseEdge path={`M ${fromX},${fromY} L ${toX},${toY}`} style={style} />
}

const edgeTypes = { threadArc: ThreadArc, threadLine: ThreadLine }

/**
 * Полупрозрачный фон под руководителем и всеми его подчинёнными — декоративный слой,
 * без обработчиков клика: сам руководитель рисуется отдельной обычной карточкой поверх
 * него (см. flatten в chartLayout.ts). Вложенные фоны накладываются друг на друга и
 * от этого выглядят насыщеннее — так видно глубину подчинения без обводок.
 */
function OrgBackgroundNode({ data }: NodeProps<Node<FlowBackgroundData>>) {
  return <div className={`org-background ${lineClass(data.color)}`} />
}

const nodeTypes = { org: OrgNode, orgBackground: OrgBackgroundNode }

/** Выгрузка схемы картинкой: снимается вьюпорт React Flow целиком, а не видимая часть. */
async function exportChart(format: 'png' | 'svg') {
  const viewport = document.querySelector<HTMLElement>('.react-flow__viewport')
  if (!viewport) return

  const { toPng, toSvg } = await import('html-to-image')
  const bounds = viewport.getBoundingClientRect()
  const options = {
    backgroundColor: getComputedStyle(document.body).backgroundColor,
    width: Math.ceil(bounds.width),
    height: Math.ceil(bounds.height),
  }

  const dataUrl = format === 'png' ? await toPng(viewport, options) : await toSvg(viewport, options)
  const link = document.createElement('a')
  link.download = `org_structure.${format}`
  link.href = dataUrl
  link.click()
}

function Canvas({
  includeDismissed,
  onOpen,
  focusId,
  placedSteps,
  placedHops,
  threading,
}: {
  includeDismissed: boolean
  onOpen: (node: ChartNode) => void
  focusId: string | null
  /** Кружки шагов, разложенные по карточкам. */
  placedSteps: Map<string, PlacedStep[]>
  placedHops: PlacedHop[]
  threading: boolean
}) {
  const flow = useReactFlow()
  const updateNodeInternals = useUpdateNodeInternals()
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const chart = useQuery({
    queryKey: ['chart', includeDismissed],
    queryFn: () => fetchChart(includeDismissed),
  })

  const toggle = useCallback((id: string) => {
    setCollapsed((previous) => {
      const next = new Set(previous)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Поиск должен не только подсветить узел, но и раскрыть путь к нему —
  // иначе найденный сотрудник останется спрятанным в свёрнутой ветке.
  useEffect(() => {
    if (!focusId || !chart.data) return
    const chain = ancestorsOf(chart.data.edges, focusId)
    setCollapsed((previous) => {
      const next = new Set(previous)
      for (const id of chain) next.delete(id)
      return next
    })
  }, [focusId, chart.data])

  // Карточка с шагами выше обычной: раскладка обязана знать об этом, иначе выросшая
  // карточка налезет на соседей — все координаты считаются от высоты.
  const heightOf = useCallback(
    (nodeId: string) => NODE_HEIGHT + threadFieldHeight(placedSteps.get(nodeId)?.length ?? 0),
    [placedSteps],
  )

  const positioned = useMemo(() => {
    if (!chart.data) return { nodes: [], backgrounds: [] }
    return layout(chart.data, collapsed, heightOf)
  }, [chart.data, collapsed, heightOf])

  const nodes = useMemo(() => {
    // Общая карта координат: фон и карточка сотрудника вычисляют свою позицию
    // относительно родителя (React Flow требует position именно так для parentId),
    // а координаты в positioned — абсолютные для canvas.
    const absolute = new Map<string, { x: number; y: number }>()
    for (const background of positioned.backgrounds) absolute.set(background.id, background)
    for (const node of positioned.nodes) absolute.set(node.id, node)

    // Фоны — раньше сотрудников: React Flow ожидает родителя (parentId) раньше
    // потомка в массиве узлов, а сотрудник всегда лежит поверх своего фона.
    const backgroundNodes: Node<FlowBackgroundData>[] = positioned.backgrounds.map(
      (background) => {
        const parent = background.parentId ? absolute.get(background.parentId) : undefined
        const position = parent
          ? { x: background.x - parent.x, y: background.y - parent.y }
          : { x: background.x, y: background.y }
        return {
          id: background.id,
          type: 'orgBackground',
          position,
          parentId: background.parentId ?? undefined,
          extent: background.parentId ? ('parent' as const) : undefined,
          draggable: false,
          selectable: false,
          data: { business_line: background.business_line, color: background.color },
          style: { width: background.width, height: background.height },
          width: background.width,
          height: background.height,
        }
      },
    )

    const personNodes: Node<FlowNodeData>[] = positioned.nodes.map((node) => {
      const parent = node.parentId ? absolute.get(node.parentId) : undefined
      const position = parent
        ? { x: node.x - parent.x, y: node.y - parent.y }
        : { x: node.x, y: node.y }
      return {
        id: node.id,
        type: 'org',
        position,
        parentId: node.parentId ?? undefined,
        extent: node.parentId ? ('parent' as const) : undefined,
        draggable: false,
        data: {
          ...node,
          highlighted: node.id === focusId,
          steps: placedSteps.get(node.id) ?? [],
          threading,
          onToggle: toggle,
          onOpen,
        },
        style: { width: node.width, height: node.height },
        width: node.width,
        height: node.height,
      }
    })

    return [...backgroundNodes, ...personNodes]
  }, [positioned.nodes, positioned.backgrounds, focusId, toggle, onOpen, placedSteps, threading])

  // Дуги нити — единственные рёбра на этой схеме. Подчинение рисуется вложенностью фонов,
  // поэтому управленческих рёбер здесь нет и добавлять их не нужно: они продублировали бы
  // то, что уже видно, и загромоздили бы путь процесса.
  const edges = useMemo<Edge[]>(() => {
    const visible = new Set(positioned.nodes.map((node) => node.id))
    return placedHops
      // Свёрнутая ветка убирает карточки с холста: дуга в исчезнувший кружок React Flow
      // отрисовать не сможет, и она молча повисла бы в углу.
      .filter(({ hop }) => visible.has(hop.source) && visible.has(hop.target))
      .map(({ hop, processId, color }) => {
        // Оба кружка в одной карточке — работа осталась у того же человека, сменился
        // только этап. Это связка нанизанных кружков, а не переход между людьми.
        const inside = hop.source === hop.target
        return {
          id: hop.id,
          source: hop.source,
          target: hop.target,
          sourceHandle: handleOf(processId, hop.from_step),
          targetHandle: handleOf(processId, hop.to_step),
          label: hop.label ?? undefined,
          type: inside ? 'threadLine' : 'threadArc',
          zIndex: 1000,
          style: { stroke: color, strokeWidth: THREAD.stroke },
          // Открытая «галочка», а не залитый треугольник: кружки маленькие, и сплошное
          // остриё рядом с ними выглядит тяжелее самой нити.
          //
          // Размер задан с поправкой на толщину линии: React Flow рисует метку в единицах
          // `strokeWidth`, поэтому у тонкой нити остриё съёжилось бы вместе с ней.
          markerEnd: inside
            ? undefined
            : {
                type: MarkerType.Arrow,
                color,
                width: 26 / THREAD.stroke,
                height: 26 / THREAD.stroke,
                strokeWidth: 1.4,
              },
        }
      })
  }, [placedHops, positioned.nodes])

  // React Flow запоминает, где у узла точки соединения, при его измерении. Кружки шагов
  // появляются, исчезают и переезжают вместе с нитью, поэтому библиотеку нужно попросить
  // перемерить узел — иначе дуги остаются привязанными к прежним координатам и тянутся
  // мимо кружков. Ошибка тихая: ничего не падает, просто концы висят не на месте.
  useEffect(() => {
    const withDots = [...placedSteps.keys()]
    if (withDots.length > 0) updateNodeInternals(withDots)
  }, [placedSteps, positioned.nodes, updateNodeInternals])

  useEffect(() => {
    if (!focusId) return
    const node = positioned.nodes.find((item) => item.id === focusId)
    if (node) {
      flow.setCenter(node.x + node.width / 2, node.y + NODE_HEIGHT / 2, {
        zoom: 1.1,
        duration: 400,
      })
    }
  }, [focusId, positioned.nodes, flow])

  if (chart.isPending) return <p className="muted">Загружаем схему…</p>
  if (chart.isError) return <p className="bad">Не удалось загрузить схему</p>

  return (
    // Размеры поля с кружками отдаются стилям переменными: определение у них одно (THREAD),
    // и CSS его не повторяет, а получает.
    <div className="chart-canvas" style={THREAD_CSS_VARS}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        minZoom={0.15}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={20} />
        <Controls showInteractive={false} />
        <MiniMap
          pannable
          zoomable
          nodeColor={minimapNodeColor}
          nodeStrokeColor={minimapNodeColor}
          nodeStrokeWidth={2}
          nodeBorderRadius={2}
          // Затемнение вне видимой области. Полупрозрачный фон страницы, а не серый
          // по умолчанию: тот в тёмной теме светлее самой миниатюры.
          maskColor="color-mix(in srgb, var(--bg) 65%, transparent)"
          maskStrokeColor="var(--oc-accent)"
          maskStrokeWidth={1}
        />
      </ReactFlow>
    </div>
  )
}

/** Список шагов нити под схемой: порядок работы плюс то, что на схему не легло. */
function ThreadLegend({ thread, color }: { thread: ChartThread; color: string }) {
  const unplaced = thread.steps.filter((step) => step.chart_node_id === null)

  return (
    <div className="card thread-legend">
      <h2>
        <span className="thread-swatch" style={{ background: color }} /> Нить процесса «
        {thread.process_name}»
      </h2>

      <ol className="thread-list">
        {thread.steps
          .filter((step) => step.chart_node_id !== null)
          .map((step) => (
            <li key={step.key} value={step.order}>
              {step.title}
              {step.resolved_via === 'department_head' && (
                <span className="muted small"> · показано на руководителе подразделения</span>
              )}
              {step.resolved_via === 'collapsed_department' && (
                <span className="muted small"> · внутри свёрнутого подразделения</span>
              )}
            </li>
          ))}
      </ol>

      {unplaced.length > 0 && (
        <p className="muted small">
          Не легли на схему:{' '}
          {unplaced.map((step) => `${step.title} (${step.unplaced_reason})`).join('; ')}. Схема
          показывает управленческое дерево названных людей, и шаг без исполнителя положить
          на неё не на что.
        </p>
      )}
    </div>
  )
}

export default function OrgChart({ currentUser }: { currentUser: UserProfile }) {
  const [includeDismissed, setIncludeDismissed] = useState(false)
  const [query, setQuery] = useState('')
  const [focusId, setFocusId] = useState<string | null>(null)
  const [selected, setSelected] = useState<number | null>(null)
  const [processIds, setProcessIds] = useState<number[]>([])
  // Панель одна: форма добавления и карточка занимают одно и то же место справа,
  // поэтому открытие одной закрывает другую.
  const [creating, setCreating] = useState(false)

  const processes = useQuery({ queryKey: ['processes', {}], queryFn: () => fetchProcesses() })

  // По запросу на процесс, а не один запрос на все: правило «кто попадает на схему»
  // считается на сервере одинаково, процессов одновременно показывают единицы, и так
  // выбор ещё одного не перезагружает уже полученные нити.
  const threads = useQueries({
    queries: processIds.map((id) => ({
      queryKey: ['chart-thread', id, includeDismissed],
      queryFn: () => fetchChartThread(id, includeDismissed),
    })),
  })

  const loaded = useMemo(
    () =>
      threads
        .map((query, index) => ({ thread: query.data, color: threadColor(index) }))
        .filter((item): item is { thread: ChartThread; color: string } => item.thread !== undefined),
    [threads],
  )

  // Кружки, разложенные по карточкам. Порядок внутри карточки — по процессу, затем по
  // номеру шага: иначе кружки одного процесса перемешались бы с чужими.
  const placedSteps = useMemo(() => {
    const index = new Map<string, PlacedStep[]>()
    for (const { thread, color } of loaded) {
      for (const step of thread.steps) {
        if (!step.chart_node_id) continue
        const placed: PlacedStep = {
          step,
          processId: thread.process_id,
          processName: thread.process_name,
          color,
          handle: handleOf(thread.process_id, step.key),
        }
        index.set(step.chart_node_id, [...(index.get(step.chart_node_id) ?? []), placed])
      }
    }
    return index
  }, [loaded])

  const placedHops = useMemo(
    () =>
      loaded.flatMap(({ thread, color }) =>
        thread.hops.map((hop) => ({ hop, processId: thread.process_id, color })),
      ),
    [loaded],
  )

  const results = useQuery({
    queryKey: ['employee-search', query, includeDismissed],
    queryFn: () => searchEmployees(query, includeDismissed),
    enabled: query.trim().length >= 2,
  })

  const chart = useQuery({
    queryKey: ['chart', includeDismissed],
    queryFn: () => fetchChart(includeDismissed),
  })

  const lines = useQuery({ queryKey: ['business-lines'], queryFn: fetchBusinessLines })

  const onOpen = useCallback((node: ChartNode) => {
    if (node.employee_id) {
      setCreating(false)
      setSelected(node.employee_id)
    }
  }, [])

  return (
    <section className="org-chart-view">
      <div className="card chart-toolbar">
        <div className="search-box">
          <input
            type="search"
            placeholder="Поиск по имени…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {results.data && query.trim().length >= 2 && (
            <ul className="search-results">
              {results.data.length === 0 && <li className="muted">Ничего не найдено</li>}
              {results.data.map((person) => (
                <li key={person.id}>
                  <button
                    type="button"
                    className="link"
                    onClick={() => {
                      setCreating(false)
                      setSelected(person.id)
                      setFocusId(person.on_chart ? `emp-${person.id}` : null)
                    }}
                  >
                    {person.display_name}
                  </button>
                  <span className="muted small"> · {person.department ?? '—'}</span>
                  {!person.on_chart && <span className="muted small"> · вне схемы</span>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <label className="toggle">
          <input
            type="checkbox"
            checked={includeDismissed}
            onChange={(event) => setIncludeDismissed(event.target.checked)}
          />
          Показать уволенных
        </label>

        {/* Нить накладывается на то же дерево, а не открывает отдельный вид: смысл в том,
            чтобы видеть путь работы по существующей структуре управления.
            Процессов можно выбрать несколько — тогда видно, где они пересекаются. */}
        <label className="toggle">
          <span className="muted small">Нить процесса</span>
          <select
            value=""
            onChange={(event) => {
              const id = Number(event.target.value)
              if (id) setProcessIds((previous) => [...previous, id])
            }}
          >
            <option value="">+ добавить…</option>
            {(processes.data ?? [])
              .filter((process) => !processIds.includes(process.id))
              .map((process) => (
                <option key={process.id} value={process.id}>
                  {process.name}
                </option>
              ))}
          </select>
        </label>

        {processIds.length > 0 && (
          <span className="thread-chips">
            {loaded.map(({ thread, color }) => (
              <button
                key={thread.process_id}
                type="button"
                className="thread-chip"
                title="Убрать нить со схемы"
                onClick={() =>
                  setProcessIds((previous) => previous.filter((id) => id !== thread.process_id))
                }
              >
                <span className="thread-swatch" style={{ background: color }} />
                {thread.process_name} ×
              </button>
            ))}
          </span>
        )}

        {currentUser.role === 'admin' && (
          <button
            type="button"
            onClick={() => {
              setSelected(null)
              setCreating(true)
            }}
          >
            + Сотрудник
          </button>
        )}

        <a className="link" href="/api/org/export.csv">
          CSV
        </a>
        <button type="button" className="link" onClick={() => exportChart('png')}>
          PNG
        </button>
        <button type="button" className="link" onClick={() => exportChart('svg')}>
          SVG
        </button>

        {chart.data && (
          <span className="muted small">
            На схеме {chart.data.nodes.length}; ещё {chart.data.off_chart_employees} человек —
            через поиск
          </span>
        )}

        <div className="chart-legend">
          {/* Легенда строится из справочника направлений, а не из зашитого списка:
              направления заводит администратор, и схема обязана следовать за ними. */}
          {lines.data?.map((line) => (
            <span className="item" key={line.id}>
              <span className={`swatch line ${lineClass(line.color)}`} /> {line.name}
            </span>
          ))}
          <span className="item">
            <span className="swatch dept" /> Свёрнутое подразделение (пунктир)
          </span>
          <span className="item">
            <span className="swatch manager" /> Руководитель — жирнее, с числом подчинённых
          </span>
          <span className="item">
            <span className="swatch scope" /> Область подчинения (полупрозрачный фон)
          </span>
        </div>
      </div>

      <ReactFlowProvider>
        <Canvas
          includeDismissed={includeDismissed}
          onOpen={onOpen}
          focusId={focusId}
          placedSteps={placedSteps}
          placedHops={placedHops}
          threading={processIds.length > 0}
        />
      </ReactFlowProvider>

      {loaded.map(({ thread, color }) => (
        <ThreadLegend key={thread.process_id} thread={thread} color={color} />
      ))}

      {creating && (
        <EmployeeCreatePanel
          onClose={() => setCreating(false)}
          onCreated={(employeeId) => {
            setCreating(false)
            setSelected(employeeId)
            setFocusId(null)
          }}
        />
      )}

      {!creating && selected && (
        <EmployeePanel
          employeeId={selected}
          currentUser={currentUser}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  )
}
